import type { ReactNode } from 'react';

import {
    generateHreflangTags,
    generateSocialMetaTags,
    getCanonicalUrl,
} from '@/utils/seo-helpers';

export interface PageSeoOptions {
    title: string;
    description: string;
    /** Active locale from Inertia shared props (e.g. 'de', 'en') */
    locale: string;
    /** Current request path from Inertia shared props */
    currentPath: string;
    robots?: string;
    image?: string;
    ogType?: string;
    structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Renders all standard page SEO head elements as direct `<Head>` children.
 *
 * Must be called inline inside `<Head>` (not wrapped in a component) so Inertia
 * SSR and client-side head management can collect the tags correctly.
 */
export function renderPageSeoElements({
    title,
    description,
    locale,
    currentPath,
    robots,
    image,
    ogType,
    structuredData,
}: PageSeoOptions): ReactNode[] {
    const canonicalUrl = getCanonicalUrl(currentPath, locale);
    const hreflangTags = generateHreflangTags(currentPath);
    const socialTags = generateSocialMetaTags({
        title,
        description,
        url: canonicalUrl,
        locale,
        image,
        type: ogType,
    });

    const elements: ReactNode[] = [
        <meta head-key="description" key="description" name="description" content={description} />,
        <link head-key="canonical" key="canonical" rel="canonical" href={canonicalUrl} />,
    ];

    if (robots) {
        elements.push(<meta head-key="robots" key="robots" name="robots" content={robots} />);
    }

    for (const tag of hreflangTags) {
        elements.push(
            <link
                head-key={`hreflang-${tag.hreflang}`}
                key={`hreflang-${tag.hreflang}`}
                rel="alternate"
                hrefLang={tag.hreflang}
                href={tag.href}
            />,
        );
    }

    for (const tag of socialTags) {
        elements.push(<meta head-key={tag.key} key={tag.key} {...tag.attrs} />);
    }

    if (structuredData) {
        const scripts = Array.isArray(structuredData) ? structuredData : [structuredData];

        scripts.forEach((data, index) => {
            elements.push(
                <script head-key={`ld+json-${index}`} key={`ld+json-${index}`} type="application/ld+json">
                    {JSON.stringify(data)}
                </script>,
            );
        });
    }

    return elements;
}
